import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {AnalyticsParameterValueOperation} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import AnalyticsParameterFieldError from 'insights/parameters/AnalyticsParameterFieldError';
import {getTypesafeParameterTypeAndConfigSettings} from 'insights/parameters/data/getParameterTypeAndConfigSetting';
import sortParameterConfigs from 'insights/parameters/data/sortParameterConfigs';
import {ParameterTypeToTSType} from 'insights/parameters/data/ValueValidators';
import {ParameterType} from 'insights/parameters/ParameterTypes';
import useAnalyticsParameterValueAndValidator from 'insights/parameters/useAnalyticsParameterValueAndValidator';
import {Title} from 'ui-library/Headers';

import {EmbeddableParameterType} from '../EmbeddingConstants';
import {ParameterValueTemplateFormQuery} from './__generated/ParameterValueTemplateFormQuery';
import ParameterValueTemplateField, {ParameterValueTemplateFieldData} from './ParameterValueTemplateField';
import {ParameterValueTemplateFormState} from './useParameterValueTemplateFormState';

type AnalyticsParameterConfig = ExtractByTypename<
  ParameterValueTemplateFormQuery['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['parameterConfigs'][number];

function ValidatedParameterValueTemplateField<T extends ParameterType>(props: {
  embeddingType: EmbeddableParameterType;
  config: AnalyticsParameterConfig;
  value: ParameterValueTemplateFieldData;
  onChange: (newValue: ParameterValueTemplateFieldData, valid: boolean) => void;
  allowAutoFocus: boolean;
}): JSX.Element {
  const {type, configSetting} = getTypesafeParameterTypeAndConfigSettings<T>(props.config);

  const {valueJsonIsValid, updateValueJson} = useAnalyticsParameterValueAndValidator(
    type,
    props.value,
    (newValue, newValid) => {
      props.onChange(
        {
          valueOperation: AnalyticsParameterValueOperation.USE_VALUE_JSON,
          valueJson: newValue as ParameterTypeToTSType[T],
        },
        newValid
      );
    }
  );

  return (
    <div>
      <ParameterValueTemplateField
        embeddingType={props.embeddingType}
        valueTemplate={props.value}
        onChange={(newValue) => {
          if (newValue.valueOperation === AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM) {
            props.onChange(newValue, true);
          } else {
            updateValueJson(newValue.valueJson);
          }
        }}
        inputProps={{
          paramConfigSetting: configSetting,
          allowAutoFocus: props.allowAutoFocus,
        }}
        paramType={type}
      />
      {!valueJsonIsValid && <AnalyticsParameterFieldError type={type} />}
    </div>
  );
}

export interface ParameterValueTemplateFieldListProps<EmbeddingType extends EmbeddableParameterType> {
  embeddingType: EmbeddingType;
  parameterConfigs: Array<AnalyticsParameterConfig>;
  parameterValueTemplateFormState: ParameterValueTemplateFormState;
  focusConfig?: Pick<AnalyticsParameterConfig, 'id'>;
}

export default function ParameterValueTemplateFieldList<EmbeddingType extends EmbeddableParameterType>(
  props: ParameterValueTemplateFieldListProps<EmbeddingType>
): JSX.Element {
  const sortedConfigs = sortParameterConfigs(props.parameterConfigs);
  // If focusConfig is supplied & in the list of configs, allowAutoFocus will set to true on that config.
  // Otherwise, allowAutoFocus will be set to true on the first config.
  const configToFocus =
    sortedConfigs.find((config) => config.id === props.focusConfig?.id) ?? sortedConfigs[0];
  return (
    <div className="well well-sm">
      {sortedConfigs.map((config, index) => (
        <div className={css(styles.inputWrapper)} key={config.id}>
          <Title>{config.systemName}</Title>
          <ValidatedParameterValueTemplateField
            embeddingType={props.embeddingType}
            config={config}
            allowAutoFocus={configToFocus.id === config.id}
            value={
              props.parameterValueTemplateFormState.getParameterValueTemplate(config.id) ?? {
                valueOperation: AnalyticsParameterValueOperation.USE_VALUE_JSON,
                valueJson: null,
              }
            }
            onChange={(newValue, newValid) => {
              props.parameterValueTemplateFormState.updateParameterValueTemplate(
                config,
                newValue.valueOperation === AnalyticsParameterValueOperation.USE_VALUE_JSON
                  ? newValue.valueJson
                  : null,
                newValue.valueOperation,
                newValid
              );
            }}
          />
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  inputWrapper: {
    marginBottom: 18,
  },
});
